import React, { useState, useMemo } from "react";
import HotelIcon from "@material-ui/icons/Hotel";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import StorageIcon from "@material-ui/icons/Storage";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ApartmentIcon from "@material-ui/icons/Business";
import Building1Interactive3D from "./Building1Interactive3D.js";

// Helper to reliably check if a room belongs to a specific building (1, 2, 3, or 4)
export const isRoomInBuilding = (r, bldgNo) => {
  if (!r || !bldgNo) return false;
  const target = String(bldgNo).replace(/\D+/g, "");
  if (!target) return false;

  // 1. Direct DormitoryID / dormitoryID
  const dormId = r.DormitoryID ?? r.dormitoryID;
  if (dormId !== undefined && dormId !== null) {
    const cleaned = String(dormId).replace(/\D+/g, "");
    if (cleaned === target) return true;
  }

  // 2. BuildingNo / buildingNo
  const bNo = r.BuildingNo ?? r.buildingNo;
  if (bNo !== undefined && bNo !== null) {
    const cleaned = String(bNo).replace(/\D+/g, "");
    if (cleaned === target) return true;
  }

  // 3. BuildingName / dormitoryName (e.g. "ตึก 1", "อาคาร 1", "หอพัก 1")
  const bName = String(r.buildingName || r.BuildingName || r.dormitoryName || r.DormitoryName || "");
  const matchBName = bName.match(/\d+/);
  if (matchBName && matchBName[0] === target) return true;

  // 4. RoomNumber first digit (e.g. 131 -> 1, 211 -> 2, 311 -> 3, 411 -> 4)
  const rNum = String(r.RoomNumber || r.roomNumber || r.roomNo || "").trim();
  if (rNum.length >= 3 && /^[1-4]/.test(rNum) && rNum.charAt(0) === target) {
    return true;
  }

  return false;
};

// Helper to extract floor number (1, 2, 3)
export const getRoomFloor = (r) => {
  const fl = r?.Floor ?? r?.floor;
  if (fl !== undefined && fl !== null && !isNaN(Number(fl))) return Number(fl);
  const rNum = String(r?.RoomNumber || r?.roomNumber || r?.roomNo || "").trim();
  if (rNum.length >= 2) {
    const parsed = parseInt(rNum.substring(1, 2), 10);
    if (!isNaN(parsed)) return parsed;
  }
  return 1;
};

// Helper to check if room has any active occupants
export const isRoomOccupied = (r) => {
  if (!r) return false;
  if (r.Status === "OCCUPIED" || r.status === "OCCUPIED") return true;
  if (Array.isArray(r.CurrentResidents) && r.CurrentResidents.length > 0) return true;
  if (Array.isArray(r.currentResidents) && r.currentResidents.length > 0) return true;
  const occ = Number(r.CurrentOccupancy ?? r.currentOccupancy ?? 0);
  return occ > 0;
};

// Color Palettes for the 3D Building Facade & Accents
const BUILDING_PALETTES = {
  sky: {
    accentHex: "#0284c7",
    bgSoft: "#f0f9ff",
    borderSoft: "#bae6fd",
    left1: "#0284c7", left2: "#0369a1",
    right1: "#0369a1", right2: "#075985",
    roof1: "#38bdf8", roof2: "#0284c7"
  },
  amber: {
    accentHex: "#d97706",
    bgSoft: "#fffbeb",
    borderSoft: "#fde68a",
    left1: "#d97706", left2: "#b45309",
    right1: "#b45309", right2: "#92400e",
    roof1: "#fbbf24", roof2: "#d97706"
  },
  purple: {
    accentHex: "#7c3aed",
    bgSoft: "#faf5ff",
    borderSoft: "#e9d5ff",
    left1: "#7c3aed", left2: "#6d28d9",
    right1: "#6d28d9", right2: "#5b21b6",
    roof1: "#a855f7", roof2: "#7c3aed"
  },
  emerald: {
    accentHex: "#059669",
    bgSoft: "#ecfdf5",
    borderSoft: "#a7f3d0",
    left1: "#059669", left2: "#047857",
    right1: "#047857", right2: "#065f46",
    roof1: "#34d399", roof2: "#059669"
  },
  navy: {
    accentHex: "#1e293b",
    bgSoft: "#f8fafc",
    borderSoft: "#cbd5e1",
    left1: "#334155", left2: "#1e293b",
    right1: "#1e293b", right2: "#0f172a",
    roof1: "#64748b", roof2: "#334155"
  }
};

export default function Dormitory3DBuildingView({
  buildings = [],
  rooms = [],
  staffCandidates = [],
  isTraineeMode = false,
  onSelectRoomCheckIn,
  onSelectRoomCheckOut,
  onSelectEditStay,
  onSelectEditRoom,
  onQuickIncreaseBedCapacity,
  onSwitchToElectricityTab,
  onOpenMasterData,
  onViewGridToggle
}) {
  const [activeBuildingId, setActiveBuildingId] = useState(1);
  const [activeFloor, setActiveFloor] = useState(1); // 1, 2, 3
  const [isExploded, setIsExploded] = useState(false);
  const [customColor, setCustomColor] = useState(null); // 'sky' | 'amber' | 'purple' | 'emerald' | 'navy'
  const [roomFilterStatus, setRoomFilterStatus] = useState("ALL"); // 'ALL' | 'AVAILABLE' | 'OCCUPIED'

  // Default color per building
  const defaultBuildingColor = useMemo(() => {
    switch (activeBuildingId) {
      case 1: return "sky";
      case 2: return "amber";
      case 3: return "purple";
      case 4: return "emerald";
      default: return "sky";
    }
  }, [activeBuildingId]);

  const activePaletteKey = customColor || defaultBuildingColor;
  const palette = BUILDING_PALETTES[activePaletteKey] || BUILDING_PALETTES.sky;

  // Find active building data
  const currentBuilding = useMemo(() => {
    return buildings.find((b) => {
      const bDorm = String(b.dormitoryID || b.buildingNo || "").replace(/\D+/g, "");
      return bDorm === String(activeBuildingId);
    }) || {
      dormitoryID: activeBuildingId,
      buildingNo: String(activeBuildingId),
      dormitoryName: `อาคาร ${activeBuildingId}`,
      shortName: `ตึก ${activeBuildingId}`,
      description: `หอพักอาคาร ${activeBuildingId} (3 ชั้น)`
    };
  }, [buildings, activeBuildingId]);

  // All rooms of this building
  const buildingRooms = useMemo(() => {
    return rooms.filter((r) => isRoomInBuilding(r, activeBuildingId));
  }, [rooms, activeBuildingId]);

  // Rooms of selected floor
  const floorRooms = useMemo(() => {
    return buildingRooms.filter((r) => getRoomFloor(r) === activeFloor);
  }, [buildingRooms, activeFloor]);

  // Stats for the selected floor
  const floorStats = useMemo(() => {
    const total = floorRooms.length;
    const occupied = floorRooms.filter(isRoomOccupied).length;
    const available = total - occupied;
    return { total, occupied, available };
  }, [floorRooms]);

  // Filtered rooms on right-hand list
  const filteredRooms = useMemo(() => {
    if (roomFilterStatus === "AVAILABLE") {
      return floorRooms.filter((r) => !isRoomOccupied(r));
    }
    if (roomFilterStatus === "OCCUPIED") {
      return floorRooms.filter(isRoomOccupied);
    }
    return floorRooms;
  }, [floorRooms, roomFilterStatus]);

  // Quick helper to check if a floor has any occupied room
  const isFloorOccupied = (fn) => {
    return buildingRooms.some((r) => getRoomFloor(r) === fn && isRoomOccupied(r));
  };

  return (
    <div className="dormitory-3d-root" style={{ fontFamily: '"Sarabun", sans-serif' }}>
      <style>{`
        .dormitory-3d-root,
        .dormitory-3d-root *,
        .bldg-card-btn,
        .bldg-card-btn * {
          font-family: "Sarabun", sans-serif !important;
        }
        .bldg-card-btn {
          cursor: pointer;
          padding: 10px 14px;
          border-radius: 14px;
          background-color: #ffffff;
          border: 2px solid #cbd5e1;
          box-shadow: 0 2px 6px rgba(0,0,0,0.04);
          transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
          position: relative;
          user-select: none;
        }
        .bldg-card-btn:hover {
          transform: translateY(-5px);
          box-shadow: 0 14px 28px -4px rgba(0, 0, 0, 0.12), 0 6px 12px -3px rgba(0, 0, 0, 0.06);
        }
        .bldg-card-btn.bldg-1:hover { border-color: #db2777; box-shadow: 0 14px 26px -4px rgba(219, 39, 119, 0.25); }
        .bldg-card-btn.bldg-2:hover { border-color: #16a34a; box-shadow: 0 14px 26px -4px rgba(22, 163, 74, 0.25); }
        .bldg-card-btn.bldg-3:hover { border-color: #d97706; box-shadow: 0 14px 26px -4px rgba(217, 119, 6, 0.25); }
        .bldg-card-btn.bldg-4:hover { border-color: #475569; box-shadow: 0 14px 26px -4px rgba(71, 85, 105, 0.25); }
        
        .bldg-card-btn.active-1 {
          background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
          border: 2.5px solid #db2777;
          transform: translateY(-5px);
          box-shadow: 0 12px 24px -4px rgba(219, 39, 119, 0.28), 0 4px 10px -2px rgba(219, 39, 119, 0.15);
        }
        .bldg-card-btn.active-2 {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border: 2.5px solid #16a34a;
          transform: translateY(-5px);
          box-shadow: 0 12px 24px -4px rgba(22, 163, 74, 0.28), 0 4px 10px -2px rgba(22, 163, 74, 0.15);
        }
        .bldg-card-btn.active-3 {
          background: linear-gradient(135deg, #fefce8 0%, #fef3c7 100%);
          border: 2.5px solid #d97706;
          transform: translateY(-5px);
          box-shadow: 0 12px 24px -4px rgba(217, 119, 6, 0.28), 0 4px 10px -2px rgba(217, 119, 6, 0.15);
        }
        .bldg-card-btn.active-4 {
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          border: 2.5px solid #475569;
          transform: translateY(-5px);
          box-shadow: 0 12px 24px -4px rgba(71, 85, 105, 0.28), 0 4px 10px -2px rgba(71, 85, 105, 0.15);
        }
      `}</style>
      
      {/* 1. TOP BUILDING CHIPS (4 BUILDINGS SELECTOR) */}
      <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "18px", backgroundColor: "#ffffff" }}>
        <div className="card-body p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
            <div className="d-flex align-items-center">
              <ApartmentIcon style={{ color: "#0284c7", marginRight: "8px", fontSize: "22px" }} />
              <strong style={{ fontSize: "0.95rem", color: "#0f172a", fontWeight: "600" }}>เลือกอาคาร (4 ตึกจริง 3 ชั้น)</strong>
              <span className="badge badge-light ml-2" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: "600", fontSize: "0.74rem", padding: "3px 8px" }}>
                คลิกสลับตึก 3D
              </span>
            </div>
            <div className="d-flex align-items-center mt-2 mt-sm-0">
              <button
                type="button"
                onClick={onViewGridToggle}
                className="btn btn-sm btn-outline-secondary font-weight-bold"
                style={{ borderRadius: "10px", fontSize: "0.8rem", padding: "4px 10px" }}
              >
                <ViewModuleIcon style={{ fontSize: "18px", marginRight: "5px" }} />
                สลับดูผังตารางรวม (Grid View)
              </button>
            </div>
          </div>

          <div className="row">
            {[1, 2, 3, 4].map((bldgNo) => {
              const isSelected = activeBuildingId === bldgNo;
              const bldgData = buildings.find((b) => Number(String(b.dormitoryID || b.buildingNo || "").replace(/\D+/g, "")) === bldgNo) || {};
              const bRooms = rooms.filter((r) => isRoomInBuilding(r, bldgNo));
              const bOcc = bRooms.filter(isRoomOccupied).length;
              const bAvail = bRooms.length - bOcc;

              let dotColor = "#db2777";
              let shortTitle = `ตึก 1: เจ้าหน้าที่ / นศ.หญิง`;
              if (bldgNo === 2) { dotColor = "#16a34a"; shortTitle = `ตึก 2: หอพักแพทย์`; }
              if (bldgNo === 3) { dotColor = "#d97706"; shortTitle = `ตึก 3: บุคลากร / ครอบครัว`; }
              if (bldgNo === 4) { dotColor = "#475569"; shortTitle = `ตึก 4: เจ้าหน้าที่ / นศ.ชาย`; }

              return (
                <div key={bldgNo} className="col-12 col-sm-6 col-lg-3 mb-2 mb-lg-0">
                  <div
                    onClick={() => {
                      setActiveBuildingId(bldgNo);
                      setActiveFloor(1);
                      setCustomColor(null);
                    }}
                    className={`bldg-card-btn bldg-${bldgNo} ${isSelected ? `active-${bldgNo}` : ""}`}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            boxShadow: `0 0 0 3px ${dotColor}33`,
                            marginRight: "8px",
                            flexShrink: 0
                          }}
                        />
                        <strong style={{ fontSize: "0.88rem", fontWeight: "600", color: "#0f172a", lineHeight: 1.35 }}>
                          {shortTitle}
                        </strong>
                      </div>
                      <span
                        style={{
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          padding: "2px 8px",
                          borderRadius: "10px",
                          backgroundColor: isSelected ? dotColor : "#f1f5f9",
                          color: isSelected ? "#ffffff" : "#334155",
                          border: isSelected ? "none" : "1.5px solid #cbd5e1",
                          whiteSpace: "nowrap"
                        }}
                      >
                        3 ชั้น
                      </span>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-between pt-2"
                      style={{ borderTop: isSelected ? `1.5px dashed ${dotColor}66` : "1.5px solid #f1f5f9" }}
                    >
                      <span style={{ fontSize: "0.78rem", fontWeight: "500", color: "#475569" }}>
                        มีในระบบ <strong style={{ color: "#0f172a", fontSize: "0.84rem" }}>{bRooms.length}</strong> ห้อง
                      </span>
                      <span
                        style={{
                          fontSize: "0.74rem",
                          fontWeight: "600",
                          padding: "2px 7px",
                          borderRadius: "6px",
                          backgroundColor: bOcc > 0 ? (isSelected ? "#fed7aa" : "#ffedd5") : (isSelected ? "#bbf7d0" : "#dcfce7"),
                          color: bOcc > 0 ? "#9a3412" : "#166534",
                          border: bOcc > 0 ? "1px solid #fdba74" : "1px solid #86efac",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {bOcc > 0 ? `● ${bOcc} พักอยู่` : "✓ ว่างทั้งหมด"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 2. MAIN WORKSPACE: IF BUILDING 1, 2, 3 OR 4 -> USE 3D INTERACTIVE ARCHITECTURE */}
      {activeBuildingId === 1 || activeBuildingId === 2 || activeBuildingId === 3 || activeBuildingId === 4 ? (
        <div className="mb-4">
          <Building1Interactive3D
            key={activeBuildingId}
            buildingId={activeBuildingId}
            buildingName={
              activeBuildingId === 1
                ? "อาคาร 1 (หอพักเจ้าหน้าที่ / นศ.หญิง)"
                : activeBuildingId === 2
                ? "อาคาร 2 (หอพักแพทย์)"
                : activeBuildingId === 3
                ? "อาคาร 3 (หอพักบุคลากร / ครอบครัว)"
                : "อาคาร 4 (หอพักเจ้าหน้าที่ / นศ.ชาย)"
            }
            theme={
              activeBuildingId === 1
                ? "pink"
                : activeBuildingId === 2
                ? "green"
                : activeBuildingId === 3
                ? "cream"
                : "gray"
            }
            hasCats={activeBuildingId === 1}
            rooms={rooms}
            staffCandidates={staffCandidates}
            isTraineeMode={isTraineeMode}
            onSelectRoomCheckIn={onSelectRoomCheckIn}
            onSelectRoomCheckOut={onSelectRoomCheckOut}
            onSelectEditStay={onSelectEditStay}
            onSelectEditRoom={onSelectEditRoom}
            onQuickIncreaseBedCapacity={onQuickIncreaseBedCapacity}
            onSwitchToElectricityTab={onSwitchToElectricityTab}
          />
        </div>
      ) : (
        <div className="row">
        
        {/* ================= LEFT COLUMN: 3D ISOMETRIC MODEL (Cols 5) ================= */}
        <div className="col-12 col-lg-5 mb-4">
          <div className="card shadow-sm border-0" style={{ borderRadius: "20px", backgroundColor: "#ffffff" }}>
            <div className="card-body p-4">
              
              {/* Header inside 3D Card */}
              <div className="d-flex align-items-center justify-content-between pb-3 mb-3 border-bottom">
                <div>
                  <div className="d-flex align-items-center">
                    <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: palette.accentHex, marginRight: "8px" }} />
                    <h5 className="mb-0 font-weight-bold" style={{ color: "#0f172a" }}>
                      {currentBuilding.dormitoryName || `อาคาร ${activeBuildingId}`}
                    </h5>
                  </div>
                  <small className="text-muted">{currentBuilding.description || "หอพัก 3 ชั้น"}</small>
                </div>

                {/* Color Palette & Exploded Buttons */}
                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center p-1 bg-light rounded mr-2" style={{ border: "1px solid #e2e8f0" }}>
                    {["sky", "amber", "purple", "emerald", "navy"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCustomColor(c)}
                        style={{
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          backgroundColor: BUILDING_PALETTES[c].accentHex,
                          border: activePaletteKey === c ? "2px solid #ffffff" : "none",
                          boxShadow: activePaletteKey === c ? "0 0 0 2px #0284c7" : "none",
                          margin: "0 3px",
                          cursor: "pointer"
                        }}
                        title={`เลือกโทนสี ${c}`}
                      />
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsExploded(!isExploded)}
                    className="btn btn-sm btn-light font-weight-bold"
                    style={{
                      borderRadius: "10px",
                      fontSize: "0.78rem",
                      backgroundColor: isExploded ? "#e0f2fe" : "#f1f5f9",
                      color: isExploded ? "#0369a1" : "#475569",
                      border: isExploded ? "1.5px solid #38bdf8" : "1px solid #cbd5e1"
                    }}
                  >
                    {isExploded ? "🏢 รวมชั้น" : "💥 แยกชั้น 3D"}
                  </button>
                </div>
              </div>

              {/* 3D Isometric SVG Container */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  minHeight: "410px",
                  borderRadius: "16px",
                  backgroundColor: palette.bgSoft,
                  border: `1.5px solid ${palette.borderSoft}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden"
                }}
              >
                {/* Active Floor Chip Overlay */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "14px",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #e2e8f0",
                    borderRadius: "20px",
                    padding: "4px 14px",
                    fontSize: "0.82rem",
                    fontWeight: "800",
                    color: palette.accentHex,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                    zIndex: 20
                  }}
                >
                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: palette.accentHex, marginRight: "6px" }} />
                  กำลังดู: ชั้น {activeFloor}
                </div>

                <div
                  style={{
                    position: "absolute",
                    top: "14px",
                    right: "14px",
                    fontSize: "0.78rem",
                    color: "#64748b",
                    fontWeight: "600",
                    zIndex: 20
                  }}
                >
                  💡 คลิกที่ตัวชั้น 1, 2, 3 ได้โดยตรง
                </div>

                {/* ================= PURE SVG 3D ARCHITECTURAL ISOMETRIC BUILDING ================= */}
                <svg
                  viewBox="0 0 520 460"
                  style={{ width: "100%", height: "360px", maxWidth: "480px", userSelect: "none" }}
                >
                  <defs>
                    <linearGradient id="svgRoofParapet" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={palette.roof1} />
                      <stop offset="100%" stopColor={palette.roof2} />
                    </linearGradient>

                    <linearGradient id="svgTopSlab" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#ffffff" />
                      <stop offset="60%" stopColor="#f1f5f9" />
                      <stop offset="100%" stopColor="#e2e8f0" />
                    </linearGradient>

                    <linearGradient id="svgActiveTop" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#e0f2fe" />
                      <stop offset="100%" stopColor="#bae6fd" />
                    </linearGradient>

                    <linearGradient id="svgLeftGlass" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={palette.left1} />
                      <stop offset="100%" stopColor={palette.left2} />
                    </linearGradient>

                    <linearGradient id="svgRightGlass" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={palette.right1} />
                      <stop offset="100%" stopColor={palette.right2} />
                    </linearGradient>

                    <linearGradient id="svgWinOcc" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fef08a" />
                      <stop offset="100%" stopColor="#f59e0b" />
                    </linearGradient>
                    <linearGradient id="svgWinAvail" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#6ee7b7" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>

                    <filter id="svgFloorShadow" x="-10%" y="-10%" width="120%" height="130%">
                      <feDropShadow dx="0" dy="18" stdDeviation="16" floodColor="#0f172a" floodOpacity="0.16" />
                    </filter>
                  </defs>

                  {/* Ground Foundation & Garden */}
                  <g filter="url(#svgFloorShadow)">
                    <polygon points="260,400 450,305 260,210 70,305" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="2" />
                    <polygon points="260,390 435,302 260,215 85,302" fill="#dcfce7" stroke="#86efac" strokeWidth="1.5" />
                    <polygon points="260,390 305,367 260,345 215,367" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1" />
                    <circle cx="110" cy="290" r="10" fill="#22c55e" stroke="#15803d" strokeWidth="1.5" />
                    <circle cx="138" cy="308" r="8" fill="#16a34a" stroke="#15803d" strokeWidth="1.5" />
                    <circle cx="410" cy="290" r="10" fill="#22c55e" stroke="#15803d" strokeWidth="1.5" />
                    <circle cx="382" cy="308" r="8" fill="#16a34a" stroke="#15803d" strokeWidth="1.5" />
                  </g>

                  {/* ================= FLOOR 1 (Ground Floor: Level 1) ================= */}
                  <g
                    onClick={() => setActiveFloor(1)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      transform: isExploded ? "translateY(8px)" : (activeFloor === 1 ? "translateY(-12px)" : "none"),
                      filter: activeFloor === 1 ? `drop-shadow(0 18px 26px ${palette.accentHex}77)` : "none"
                    }}
                  >
                    <polygon points="120,312 260,382 260,372 120,302" fill="#94a3b8" />
                    <polygon points="260,382 400,312 400,302 260,372" fill="#64748b" />
                    <polygon points="260,372 400,302 260,232 120,302" fill="url(#svgTopSlab)" stroke="#cbd5e1" strokeWidth="1" />

                    <polygon points="120,302 260,372 260,302 120,232" fill="url(#svgLeftGlass)" stroke="#0f172a" strokeOpacity="0.25" strokeWidth="1.5" />
                    <polygon points="260,372 400,302 400,232 260,302" fill="url(#svgRightGlass)" stroke="#0f172a" strokeOpacity="0.25" strokeWidth="1.5" />

                    <polygon
                      points="260,302 400,232 260,162 120,232"
                      fill={activeFloor === 1 ? "url(#svgActiveTop)" : "url(#svgTopSlab)"}
                      stroke={activeFloor === 1 ? "#38bdf8" : "#94a3b8"}
                      strokeWidth={activeFloor === 1 ? 2.5 : 1.5}
                    />

                    {/* Floor 1 Windows */}
                    <polygon points="140,248 175,265 175,282 140,265" fill={isFloorOccupied(1) ? "url(#svgWinOcc)" : "url(#svgWinAvail)"} stroke="#059669" strokeWidth="1" />
                    <polygon points="185,270 215,285 215,302 185,287" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="225,290 250,302 250,320 225,307" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />

                    <polygon points="270,315 310,295 310,345 270,365" fill="#bae6fd" stroke="#0284c7" strokeWidth="2" />
                    <line x1="290" y1="305" x2="290" y2="355" stroke="#0284c7" strokeWidth="1.5" />
                    <polygon points="325,288 355,273 355,298 325,313" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="365,268 390,255 390,280 365,293" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />

                    {/* Floor 1 Callout Pill */}
                    <g transform="translate(50, 275)">
                      <rect x="0" y="0" width="76" height="26" rx="13" fill={activeFloor === 1 ? palette.accentHex : "#475569"} />
                      <text x="38" y="17" fill="white" fontSize="11" fontWeight="800" textAnchor="middle">ชั้น 1 (1F)</text>
                    </g>
                  </g>

                  {/* ================= FLOOR 2 (Mid Floor: Level 2) ================= */}
                  <g
                    onClick={() => setActiveFloor(2)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      transform: isExploded ? "translateY(-12px)" : (activeFloor === 2 ? "translateY(-12px)" : "none"),
                      filter: activeFloor === 2 ? `drop-shadow(0 18px 26px ${palette.accentHex}77)` : "none"
                    }}
                  >
                    <polygon points="120,242 260,312 260,302 120,232" fill="#94a3b8" />
                    <polygon points="260,312 400,242 400,232 260,302" fill="#64748b" />
                    <polygon points="260,302 400,232 260,162 120,232" fill="url(#svgTopSlab)" stroke="#cbd5e1" strokeWidth="1" />

                    <polygon points="120,232 260,302 260,232 120,162" fill="url(#svgLeftGlass)" stroke="#0f172a" strokeOpacity="0.25" strokeWidth="1.5" />
                    <polygon points="260,302 400,232 400,162 260,232" fill="url(#svgRightGlass)" stroke="#0f172a" strokeOpacity="0.25" strokeWidth="1.5" />

                    <polygon
                      points="260,232 400,162 260,92 120,162"
                      fill={activeFloor === 2 ? "url(#svgActiveTop)" : "url(#svgTopSlab)"}
                      stroke={activeFloor === 2 ? "#38bdf8" : "#94a3b8"}
                      strokeWidth={activeFloor === 2 ? 2.5 : 1.5}
                    />

                    {/* Floor 2 Windows */}
                    <polygon points="140,178 175,195 175,215 140,198" fill={isFloorOccupied(2) ? "url(#svgWinOcc)" : "url(#svgWinAvail)"} stroke="#059669" strokeWidth="1" />
                    <polygon points="185,200 215,215 215,235 185,220" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="225,220 250,232 250,252 225,240" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />

                    <polygon points="265,232 295,217 295,237 265,252" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="305,212 340,195 340,215 305,232" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="350,190 385,172 385,192 350,210" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />

                    {/* Floor 2 Callout Pill */}
                    <g transform="translate(50, 205)">
                      <rect x="0" y="0" width="76" height="26" rx="13" fill={activeFloor === 2 ? palette.accentHex : "#475569"} />
                      <text x="38" y="17" fill="white" fontSize="11" fontWeight="800" textAnchor="middle">ชั้น 2 (2F)</text>
                    </g>
                  </g>

                  {/* ================= FLOOR 3 (Top Floor: Level 3 - NO ROOFTOP) ================= */}
                  <g
                    onClick={() => setActiveFloor(3)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      transform: isExploded ? "translateY(-34px)" : (activeFloor === 3 ? "translateY(-12px)" : "none"),
                      filter: activeFloor === 3 ? `drop-shadow(0 18px 26px ${palette.accentHex}77)` : "none"
                    }}
                  >
                    <polygon points="120,172 260,242 260,232 120,162" fill="#94a3b8" />
                    <polygon points="260,242 400,172 400,162 260,232" fill="#64748b" />
                    <polygon points="260,232 400,162 260,92 120,162" fill="url(#svgTopSlab)" stroke="#cbd5e1" strokeWidth="1" />

                    <polygon points="120,162 260,232 260,162 120,92" fill="url(#svgLeftGlass)" stroke="#0f172a" strokeOpacity="0.25" strokeWidth="1.5" />
                    <polygon points="260,232 400,162 400,92 260,162" fill="url(#svgRightGlass)" stroke="#0f172a" strokeOpacity="0.25" strokeWidth="1.5" />

                    <polygon
                      points="260,162 400,92 260,22 120,92"
                      fill={activeFloor === 3 ? "url(#svgActiveTop)" : "url(#svgTopSlab)"}
                      stroke={activeFloor === 3 ? "#38bdf8" : "#94a3b8"}
                      strokeWidth={activeFloor === 3 ? 2.5 : 1.5}
                    />

                    {/* Floor 3 Parapet Edge Finish */}
                    <polygon points="125,92 260,160 395,92 395,84 260,152 125,84" fill="url(#svgRoofParapet)" stroke="#0284c7" strokeWidth="1" opacity="0.9" />

                    {/* Floor 3 Windows */}
                    <polygon points="145,115 185,135 185,157 145,137" fill={isFloorOccupied(3) ? "url(#svgWinOcc)" : "url(#svgWinAvail)"} stroke="#059669" strokeWidth="1" />
                    <polygon points="205,145 245,165 245,187 205,167" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="270,165 310,145 310,167 270,187" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />
                    <polygon points="330,135 370,115 370,137 330,157" fill="url(#svgWinAvail)" stroke="#059669" strokeWidth="1" />

                    {/* Floor 3 Callout Pill */}
                    <g transform="translate(50, 135)">
                      <rect x="0" y="0" width="76" height="26" rx="13" fill={activeFloor === 3 ? palette.accentHex : "#475569"} />
                      <text x="38" y="17" fill="white" fontSize="11" fontWeight="800" textAnchor="middle">ชั้น 3 (3F)</text>
                    </g>
                  </g>
                </svg>
              </div>

              {/* Floor Switcher Quick Buttons (ชั้น 1 ซ้าย ไป ชั้น 3 ขวา) */}
              <div className="d-flex align-items-center mt-3 bg-light p-2 rounded" style={{ border: "1px solid #e2e8f0" }}>
                {[1, 2, 3].map((fn) => {
                  const isActive = activeFloor === fn;
                  return (
                    <button
                      key={fn}
                      type="button"
                      onClick={() => setActiveFloor(fn)}
                      className={`btn btn-sm flex-fill font-weight-bold ${isActive ? 'btn-primary' : 'btn-light'}`}
                      style={{
                        borderRadius: "10px",
                        margin: "0 3px",
                        backgroundColor: isActive ? palette.accentHex : "#ffffff",
                        borderColor: isActive ? palette.accentHex : "#cbd5e1",
                        color: isActive ? "#ffffff" : "#334155"
                      }}
                    >
                      กดดูชั้น {fn}
                    </button>
                  );
                })}
              </div>

            </div>
          </div>
        </div>

        {/* ================= RIGHT COLUMN: INTERACTIVE ROOM LIST (Cols 7) ================= */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0" style={{ borderRadius: "20px", backgroundColor: "#ffffff", minHeight: "580px" }}>
            <div className="card-body p-4 d-flex flex-column">

              {/* Floor Header Bar */}
              <div className="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-3 border-bottom">
                <div>
                  <div className="d-flex align-items-center">
                    <span
                      className="badge text-white font-weight-bold px-3 py-1 mr-2"
                      style={{ borderRadius: "20px", backgroundColor: palette.accentHex, fontSize: "0.85rem" }}
                    >
                      Floor {activeFloor}
                    </span>
                    <h5 className="mb-0 font-weight-bold" style={{ color: "#0f172a" }}>
                      {currentBuilding.shortName || `ตึก ${activeBuildingId}`} • ผังห้องพักชั้น {activeFloor}
                    </h5>
                  </div>
                  <small className="text-muted">
                    แสดงรายการห้องพักจริงของชั้น {activeFloor} (คลิกดูประวัติ หรือจัดคนเข้าห้อง)
                  </small>
                </div>

                {/* Floor Mini KPI Badges */}
                <div className="d-flex align-items-center mt-2 mt-sm-0">
                  <div className="text-center px-3 py-1 mr-2 rounded" style={{ backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0" }}>
                    <small className="d-block font-weight-bold text-success" style={{ fontSize: "0.7rem" }}>ห้องว่าง</small>
                    <strong style={{ fontSize: "1.1rem", color: "#065f46" }}>{floorStats.available}</strong>
                  </div>
                  <div className="text-center px-3 py-1 mr-2 rounded" style={{ backgroundColor: "#eff6ff", border: "1px solid #bfdbfe" }}>
                    <small className="d-block font-weight-bold text-primary" style={{ fontSize: "0.7rem" }}>มีผู้พัก</small>
                    <strong style={{ fontSize: "1.1rem", color: "#1e40af" }}>{floorStats.occupied}</strong>
                  </div>
                  <div className="text-center px-3 py-1 rounded bg-light" style={{ border: "1px solid #cbd5e1" }}>
                    <small className="d-block font-weight-bold text-muted" style={{ fontSize: "0.7rem" }}>รวมชั้นนี้</small>
                    <strong style={{ fontSize: "1.1rem", color: "#1e293b" }}>{floorStats.total}</strong>
                  </div>
                </div>
              </div>

              {/* Status Filter Buttons */}
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    onClick={() => setRoomFilterStatus("ALL")}
                    className={`btn font-weight-bold ${roomFilterStatus === "ALL" ? 'btn-primary' : 'btn-light'}`}
                    style={{
                      backgroundColor: roomFilterStatus === "ALL" ? palette.accentHex : "#ffffff",
                      borderColor: roomFilterStatus === "ALL" ? palette.accentHex : "#cbd5e1",
                      borderRadius: "8px 0 0 8px"
                    }}
                  >
                    ทั้งหมด ({floorStats.total})
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoomFilterStatus("AVAILABLE")}
                    className={`btn font-weight-bold ${roomFilterStatus === "AVAILABLE" ? 'btn-primary' : 'btn-light'}`}
                    style={{
                      backgroundColor: roomFilterStatus === "AVAILABLE" ? palette.accentHex : "#ffffff",
                      borderColor: roomFilterStatus === "AVAILABLE" ? palette.accentHex : "#cbd5e1"
                    }}
                  >
                    ว่างพร้อมพัก ({floorStats.available})
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoomFilterStatus("OCCUPIED")}
                    className={`btn font-weight-bold ${roomFilterStatus === "OCCUPIED" ? 'btn-primary' : 'btn-light'}`}
                    style={{
                      backgroundColor: roomFilterStatus === "OCCUPIED" ? palette.accentHex : "#ffffff",
                      borderColor: roomFilterStatus === "OCCUPIED" ? palette.accentHex : "#cbd5e1",
                      borderRadius: "0 8px 8px 0"
                    }}
                  >
                    มีคนพักอยู่ ({floorStats.occupied})
                  </button>
                </div>

                <span className="badge badge-info text-white px-2.5 py-1" style={{ backgroundColor: "#0284c7", fontSize: "0.78rem" }}>
                  ✓ ผังห้องพักตามผังจริง
                </span>
              </div>

              {/* Room Cards List */}
              <div className="flex-fill" style={{ maxHeight: "500px", overflowY: "auto" }}>
                <div className="row">
                  {filteredRooms.map((room) => {
                    const isOcc = room.Status === "OCCUPIED" || (room.CurrentResidents && room.CurrentResidents.length > 0);
                    const bedCapacity = room.BedCapacity || room.bedCapacity || 2;
                    const occupancy = (room.CurrentResidents && room.CurrentResidents.length) || (isOcc ? 1 : 0);

                    return (
                      <div key={room.RoomID || room.roomID} className="col-12 col-md-6 mb-3">
                        <div
                          style={{
                            padding: "16px",
                            borderRadius: "16px",
                            border: isOcc ? "2px solid #bfdbfe" : "2px solid #a7f3d0",
                            backgroundColor: isOcc ? "#f8fafc" : "#ffffff",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                            transition: "all 0.2s ease"
                          }}
                        >
                          {/* Room Number & Status */}
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center">
                              <span style={{ fontSize: "1.25rem", fontWeight: "700", color: "#0f172a", marginRight: "8px" }}>
                                ห้อง {room.RoomNumber || room.roomNumber}
                              </span>
                              <span
                                className="badge px-2 py-0.5"
                                style={{
                                  backgroundColor: room.TargetGroup === "STAFF" ? "#ede9fe" : "#ccfbf1",
                                  color: room.TargetGroup === "STAFF" ? "#5b21b6" : "#0f766e",
                                  fontWeight: "700",
                                  fontSize: "0.72rem"
                                }}
                              >
                                {room.RoomType || room.roomType || (room.TargetGroup === "STAFF" ? "เจ้าหน้าที่" : "นักศึกษา")}
                              </span>
                            </div>

                            <span
                              className={`badge px-2.5 py-1 ${isOcc ? 'badge-primary' : 'badge-success'}`}
                              style={{
                                backgroundColor: isOcc ? "#2563eb" : "#059669",
                                fontSize: "0.75rem",
                                fontWeight: "700"
                              }}
                            >
                              {isOcc ? "● มีผู้พัก" : "✓ ว่างพร้อมพัก"}
                            </span>
                          </div>

                          {/* Bed Capacity Slots */}
                          <div
                            className="d-flex align-items-center justify-content-between p-2 rounded mb-2 bg-light"
                            style={{ fontSize: "0.78rem", border: "1px solid #e2e8f0" }}
                          >
                            <span className="text-muted">🛏️ ขนาด: <strong>{bedCapacity} เตียง</strong></span>
                            <div className="d-flex align-items-center">
                              <small className="text-muted mr-1 font-weight-bold">เตียง:</small>
                              {Array.from({ length: bedCapacity }).map((_, i) => (
                                <span
                                  key={i}
                                  className="badge mr-1"
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    lineHeight: "14px",
                                    borderRadius: "4px",
                                    backgroundColor: i < occupancy ? "#2563eb" : "#dcfce7",
                                    color: i < occupancy ? "#ffffff" : "#15803d",
                                    border: i < occupancy ? "none" : "1px solid #86efac",
                                    fontSize: "0.7rem",
                                    fontWeight: "800"
                                  }}
                                  title={i < occupancy ? "เตียงมีผู้พัก" : "เตียงว่าง"}
                                >
                                  {i + 1}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Residents Roster */}
                          <div
                            className="p-2 rounded mb-3 bg-white"
                            style={{ border: "1px solid #e2e8f0", minHeight: "52px" }}
                          >
                            <small className="d-block text-muted font-weight-bold" style={{ fontSize: "0.68rem", textTransform: "uppercase" }}>
                              ผู้พักอาศัยปัจจุบัน
                            </small>
                            {isOcc && room.CurrentResidents && room.CurrentResidents.length > 0 ? (
                              room.CurrentResidents.map((res, idx) => (
                                <div key={idx} className="d-flex align-items-center justify-content-between mb-1 py-1 px-2 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                                  <div className="font-weight-bold text-truncate mr-2" style={{ fontSize: "0.82rem", color: "#1e293b" }}>
                                    👤 {res.TraineeName || res.traineeName} <small className="text-muted">({res.PositionText || res.positionText || "ผู้เข้าพัก"})</small>
                                  </div>
                                  <div className="d-flex align-items-center" style={{ gap: "4px", flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      onClick={() => onSelectEditStay && onSelectEditStay(res, room)}
                                      className="btn btn-sm btn-outline-primary font-weight-bold"
                                      style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: "6px" }}
                                      title="แก้ไขข้อมูลการเข้าพัก"
                                    >
                                      แก้ไข
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => onSelectRoomCheckOut && onSelectRoomCheckOut(res, room)}
                                      className="btn btn-sm btn-outline-danger font-weight-bold"
                                      style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: "6px" }}
                                      title="แจ้งย้ายออกจากห้อง"
                                    >
                                      ย้ายออก
                                    </button>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <small className="text-success font-italic font-weight-bold">✓ ห้องว่าง สามารถจัดคนเข้าพักได้ทันที</small>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex align-items-center pt-2 border-top">
                            {isOcc ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => onSwitchToElectricityTab && onSwitchToElectricityTab(room)}
                                  className="btn btn-sm btn-primary flex-fill font-weight-bold mr-2"
                                  style={{ fontSize: "0.78rem", borderRadius: "8px" }}
                                >
                                  ดูมิเตอร์ไฟ / บันทึกหน่วย
                                </button>
                                {occupancy < bedCapacity && (
                                  <button
                                    type="button"
                                    onClick={() => onSelectRoomCheckIn && onSelectRoomCheckIn(room)}
                                    className="btn btn-sm btn-outline-success font-weight-bold"
                                    style={{ fontSize: "0.78rem", borderRadius: "8px" }}
                                    title="มีเตียงว่าง สามารถเพิ่มผู้เข้าพักได้อีก"
                                  >
                                    + เพิ่มคน
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => onSelectRoomCheckIn && onSelectRoomCheckIn(room)}
                                  className="btn btn-sm btn-success flex-fill font-weight-bold mr-2"
                                  style={{ fontSize: "0.78rem", borderRadius: "8px" }}
                                >
                                  + จัดคนเข้าห้องนี้
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onOpenMasterData && onOpenMasterData()}
                                  className="btn btn-sm btn-outline-secondary font-weight-bold"
                                  style={{ fontSize: "0.78rem", borderRadius: "8px" }}
                                >
                                  ตั้งค่า
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}


                </div>
              </div>

              {/* Bottom Quick Bar */}
              <div
                className="d-flex align-items-center justify-content-between flex-wrap pt-3 mt-3 border-top bg-light p-2 rounded"
                style={{ fontSize: "0.8rem" }}
              >
                <div className="text-muted">
                  <span className="text-success font-weight-bold">● เชื่อมโยงระบบฐานข้อมูลจริง</span> • มีห้องในชั้นนี้ {floorRooms.length} ห้อง
                </div>
                <button
                  type="button"
                  onClick={onOpenMasterData}
                  className="btn btn-sm btn-outline-primary font-weight-bold"
                  style={{ borderRadius: "8px", fontSize: "0.78rem" }}
                >
                  <StorageIcon style={{ fontSize: "16px", marginRight: "4px" }} />
                  ไปหน้าจัดการข้อมูลหลัก (Master Data)
                </button>
              </div>

            </div>
          </div>
        </div>

      </div>
      )}

    </div>
  );
}
